import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { chemistryApi } from '../../api/chemistry';
import { StructureEditorModal } from './StructureEditor';

// Column statistics from the grid
export interface ColumnStats {
  [columnName: string]: {
    min: number;
    max: number;
    isNumeric: boolean;
  };
}

// Per-column heatmap configuration
export interface HeatmapColumnConfig {
  enabled: boolean;
  min: number;  // Actual value threshold
  max: number;  // Actual value threshold
}

export interface HeatmapSettings {
  intensity: number;  // Opacity: 0-1
  columns: {
    [columnName: string]: HeatmapColumnConfig;
  };
}

interface SelectedRow {
  id: number;
  smiles?: string;
  properties?: Record<string, any>;
}

interface TableManagementDropdownProps {
  columns: string[];
  getGridColumns?: () => string[];
  selectedCount: number;
  selectedRows?: SelectedRow[];  // Actual selected row data
  fileId?: string;
  onDeduplicate: () => void;
  onRefresh?: () => void;
  isTransposed?: boolean;
  onToggleTranspose?: () => void;
  showHeatmap?: boolean;
  onToggleHeatmap?: () => void;
  heatmapSettings?: HeatmapSettings;
  onHeatmapSettingsChange?: (settings: HeatmapSettings) => void;
  columnStats?: ColumnStats;  // Column statistics from the grid
  onOpenHeatmapPanel?: () => void;  // Open floating heatmap settings panel
  onResetColumnWidths?: () => void;  // Reset column widths to defaults
  onColumnVisibilityChange?: (columnId: string, visible: boolean) => void;  // Toggle column visibility
}

// Calculated column options
const CALCULATED_COLUMNS = [
  { id: 'mw', label: 'Molecular Weight (MW)' },
  { id: 'formula', label: 'Molecular Formula' },
  { id: 'logp', label: 'LogP' },
  { id: 'tpsa', label: 'TPSA' },
  { id: 'hbd', label: 'H-Bond Donors (HBD)' },
  { id: 'hba', label: 'H-Bond Acceptors (HBA)' },
  { id: 'rotbonds', label: 'Rotatable Bonds' },
  { id: 'rings', label: 'Ring Count' },
];

const TableManagementDropdown: React.FC<TableManagementDropdownProps> = ({
  columns,
  getGridColumns,
  selectedCount,
  selectedRows = [],
  fileId,
  onDeduplicate,
  onRefresh,
  isTransposed,
  onToggleTranspose,
  showHeatmap,
  onToggleHeatmap,
  heatmapSettings = { intensity: 0.4, columns: {} },
  onHeatmapSettingsChange,
  columnStats = {},
  onOpenHeatmapPanel,
  onResetColumnWidths,
  onColumnVisibilityChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  // Live columns — refreshed from AG Grid API every time dropdown opens or section expands
  const [liveColumns, setLiveColumns] = useState<string[]>(columns);

  // Refresh live columns from grid API
  const refreshLiveColumns = useCallback(() => {
    const fresh = getGridColumns ? getGridColumns() : columns;
    console.log('[TableManagement] refreshLiveColumns called, got:', fresh);
    console.log('[TableManagement] gridApi available:', !!getGridColumns, '| fallback columns:', columns.length);
    setLiveColumns(fresh);
  }, [getGridColumns, columns]);

  // Refresh when dropdown opens
  useEffect(() => {
    if (isOpen) refreshLiveColumns();
  }, [isOpen, refreshLiveColumns]);

  // Refresh when a section expands
  useEffect(() => {
    if (expandedSection) refreshLiveColumns();
  }, [expandedSection, refreshLiveColumns]);

  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(new Set(columns));

  // Sync visibleColumns when liveColumns changes — new columns default to visible
  useEffect(() => {
    setVisibleColumns(prev => {
      const next = new Set(prev);
      let changed = false;
      liveColumns.forEach(col => {
        if (!next.has(col)) { next.add(col); changed = true; }
      });
      return changed ? next : prev;
    });
  }, [liveColumns]);

  const [toast, setToast] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Modal states for Add Row and Add Column
  const [showAddRowModal, setShowAddRowModal] = useState(false);
  const [showAddColumnModal, setShowAddColumnModal] = useState(false);
  const [newRowSmiles, setNewRowSmiles] = useState('');
  const [newColumnName, setNewColumnName] = useState('');
  const [newColumnDefault, setNewColumnDefault] = useState('');

  // Modal state for Remove Column confirmation
  const [showRemoveColumnModal, setShowRemoveColumnModal] = useState(false);
  const [removeColumnTarget, setRemoveColumnTarget] = useState('');

  // Modal states for Delete, Bulk Edit, Export
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [bulkEditProperty, setBulkEditProperty] = useState('');
  const [bulkEditValue, setBulkEditValue] = useState('');
  const [newPropertyName, setNewPropertyName] = useState('');

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const clickedButton = buttonRef.current?.contains(target);
      const clickedMenu = menuRef.current?.contains(target);
      if (!clickedButton && !clickedMenu) {
        setIsOpen(false);
        setExpandedSection(null);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Calculate menu position with viewport bounds checking
  const updateMenuPosition = useCallback(() => {
    if (!buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const menuWidth = 280;
    const menuHeight = 500; // Approximate max menu height
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const padding = 8;

    // Horizontal positioning - align right edge with button, but stay in viewport
    let left = rect.right - menuWidth;
    if (left < padding) left = padding;
    if (left + menuWidth > viewportWidth - padding) left = viewportWidth - menuWidth - padding;

    // Vertical positioning - prefer below button, flip above if needed
    let top = rect.bottom + 4;
    const spaceBelow = viewportHeight - rect.bottom - padding;
    const spaceAbove = rect.top - padding;

    // If not enough space below and more space above, flip to above
    if (spaceBelow < menuHeight && spaceAbove > spaceBelow) {
      top = Math.max(padding, rect.top - Math.min(menuHeight, spaceAbove) - 4);
    }

    setMenuPos({ top, left });
  }, []);

  // Update position when menu opens and on window resize
  useEffect(() => {
    if (isOpen) {
      updateMenuPosition();
      const handleScroll = (e: Event) => {
        // Don't reposition if scrolling inside the dropdown
        if (menuRef.current && menuRef.current.contains(e.target as Node)) return;
        updateMenuPosition();
      };
      window.addEventListener('resize', updateMenuPosition);
      window.addEventListener('scroll', handleScroll, true);
      return () => {
        window.removeEventListener('resize', updateMenuPosition);
        window.removeEventListener('scroll', handleScroll, true);
      };
    }
  }, [isOpen, updateMenuPosition]);

  // Auto-hide toast
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const closeMenu = () => {
    setIsOpen(false);
    setExpandedSection(null);
  };

  const showComingSoon = (feature: string) => {
    console.log(`Feature: ${feature} clicked`);
    setToast(`${feature} - Coming soon`);
    closeMenu();
  };

  const handleAddColumn = () => {
    closeMenu();
    setNewColumnName('');
    setNewColumnDefault('');
    setShowAddColumnModal(true);
  };

  const handleSubmitAddColumn = async () => {
    if (!fileId || !newColumnName.trim()) {
      setToast('Column name is required');
      return;
    }

    setIsLoading(true);
    setToast(`Adding column "${newColumnName}"...`);
    setShowAddColumnModal(false);

    try {
      const result = await chemistryApi.addCustomColumn(fileId, newColumnName.trim(), newColumnDefault || null);
      setToast(`Added "${newColumnName}" column to ${result.updatedCount} molecules`);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Failed to add custom column:', error);
      setToast(`Failed to add column "${newColumnName}"`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddCalculatedColumn = async (type: string, label: string) => {
    if (!fileId) {
      setToast('No file selected');
      return;
    }

    console.log(`Feature: Add Calculated Column - ${type} clicked`);
    closeMenu();
    setIsLoading(true);
    setToast(`Adding ${label} column...`);

    try {
      const result = await chemistryApi.addCalculatedColumn(fileId, type);
      setToast(`Added ${result.columnName} column to ${result.updatedCount} molecules`);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Failed to add calculated column:', error);
      setToast(`Failed to add ${label} column`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleColumn = (column: string) => {
    const newVisible = new Set(visibleColumns);
    const isVisible = newVisible.has(column);
    if (isVisible) {
      newVisible.delete(column);
    } else {
      newVisible.add(column);
    }
    setVisibleColumns(newVisible);
    if (onColumnVisibilityChange) {
      onColumnVisibilityChange(column, !isVisible);
    }
    setToast(`${isVisible ? 'Hidden' : 'Showing'} column: ${column}`);
  };

  const handleShowAllColumns = () => {
    setVisibleColumns(new Set(liveColumns));
    liveColumns.forEach(col => {
      if (onColumnVisibilityChange) {
        onColumnVisibilityChange(col, true);
      }
    });
    setToast('All columns visible');
    closeMenu();
  };

  const handleRemoveColumn = (column: string) => {
    closeMenu();
    setRemoveColumnTarget(column);
    setShowRemoveColumnModal(true);
  };

  const handleConfirmRemoveColumn = () => {
    if (!removeColumnTarget) return;
    const newVisible = new Set(visibleColumns);
    newVisible.delete(removeColumnTarget);
    setVisibleColumns(newVisible);
    if (onColumnVisibilityChange) {
      onColumnVisibilityChange(removeColumnTarget, false);
    }
    setToast(`Removed column: ${removeColumnTarget}`);
    setShowRemoveColumnModal(false);
    setRemoveColumnTarget('');
  };

  const handleAddRow = () => {
    closeMenu();
    setNewRowSmiles('');
    setShowAddRowModal(true);
  };

  const handleAddBlankRow = async () => {
    closeMenu();
    if (!fileId) {
      setToast('No file selected');
      return;
    }
    setIsLoading(true);
    setToast('Adding blank row...');
    try {
      const result = await chemistryApi.addMolecule(fileId, {
        structure: '',
        molproperties: {},
      });
      setToast(`Added ${result.length} blank row(s)`);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Failed to add blank row:', error);
      setToast('Failed to add blank row');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitAddRow = async () => {
    if (!fileId) {
      setToast('No file selected');
      return;
    }

    setIsLoading(true);
    setToast('Adding molecule...');
    setShowAddRowModal(false);

    try {
      const result = await chemistryApi.addMolecule(fileId, {
        structure: newRowSmiles.trim(),
        molproperties: {},
      });
      setToast(`Added ${result.length} molecule(s) successfully`);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Failed to add molecule:', error);
      setToast('Failed to add molecule');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteSelectedRows = () => {
    if (selectedCount === 0) {
      setToast('No rows selected');
      return;
    }
    closeMenu();
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!fileId || selectedRows.length === 0) return;

    setIsLoading(true);
    setShowDeleteModal(false);
    setToast(`Deleting ${selectedRows.length} molecules...`);

    try {
      const moleculeIds = selectedRows.map(r => r.id);
      const result = await chemistryApi.deleteMolecules(fileId, moleculeIds);
      setToast(`Deleted ${result.deletedCount} molecules`);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Failed to delete molecules:', error);
      setToast('Failed to delete molecules');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBulkEdit = () => {
    if (selectedCount === 0) {
      setToast('No rows selected for bulk edit');
      return;
    }
    closeMenu();
    setBulkEditProperty('');
    setBulkEditValue('');
    setNewPropertyName('');
    setShowBulkEditModal(true);
  };

  const handleConfirmBulkEdit = async () => {
    const effectiveProperty = bulkEditProperty === '__new__' ? newPropertyName.trim() : bulkEditProperty.trim();
    if (!fileId || selectedRows.length === 0 || !effectiveProperty) {
      setToast('Property name is required');
      return;
    }

    setIsLoading(true);
    setShowBulkEditModal(false);
    setToast(`Updating ${selectedRows.length} molecules...`);

    try {
      const moleculeIds = selectedRows.map(r => r.id);
      const result = await chemistryApi.bulkEditMolecules(
        fileId,
        moleculeIds,
        effectiveProperty,
        bulkEditValue
      );
      setToast(`Updated ${result.updatedCount} molecules`);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Failed to bulk edit:', error);
      setToast('Failed to update molecules');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeduplicateClick = () => {
    onDeduplicate();
    closeMenu();
  };

  const handleCopySelected = async () => {
    if (selectedCount === 0) {
      setToast('No rows selected to copy');
      return;
    }
    if (!fileId) {
      setToast('No file selected');
      return;
    }

    closeMenu();
    setIsLoading(true);
    setToast(`Duplicating ${selectedRows.length} molecules...`);

    try {
      const moleculeIds = selectedRows.map(r => r.id);
      const result = await chemistryApi.duplicateMolecules(fileId, moleculeIds);
      setToast(`Duplicated ${result.duplicatedCount} molecules`);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Failed to duplicate molecules:', error);
      setToast('Failed to duplicate molecules');
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportSelected = () => {
    if (selectedCount === 0) {
      setToast('No rows selected to export');
      return;
    }

    closeMenu();

    // Build CSV from selected rows
    const headers = ['id', 'smiles', ...liveColumns];
    const csvRows = [headers.join(',')];

    selectedRows.forEach(row => {
      const values = [
        row.id,
        `"${(row.smiles || '').replace(/"/g, '""')}"`,
        ...liveColumns.map(col => {
          const val = row.properties?.[col] ?? '';
          return typeof val === 'string' ? `"${val.replace(/"/g, '""')}"` : val;
        })
      ];
      csvRows.push(values.join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `selected_molecules_${selectedRows.length}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    setToast(`Exported ${selectedRows.length} molecules to CSV`);
  };

  const handleToggleTranspose = () => {
    console.log('[TableManagement] handleToggleTranspose called, isTransposed:', isTransposed, 'onToggleTranspose:', !!onToggleTranspose);
    if (onToggleTranspose) {
      onToggleTranspose();
      console.log('[TableManagement] onToggleTranspose executed');
    }
    closeMenu();
  };

  const handleToggleHeatmap = () => {
    if (onToggleHeatmap) onToggleHeatmap();
    // Don't close menu when enabling heatmap - keep it open so user can adjust sliders
    if (showHeatmap) closeMenu(); // Only close when disabling
  };

  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  // Simple menu item
  const MenuItem: React.FC<{
    label: string;
    onClick?: () => void;
    disabled?: boolean;
    icon?: React.ReactNode;
    checked?: boolean;
  }> = ({ label, onClick, disabled, icon, checked }) => (
    <button
      className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 ${
        disabled ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'
      }`}
      onClick={onClick}
      disabled={disabled}
    >
      {checked !== undefined ? (
        <span className={`w-4 h-4 flex items-center justify-center ${checked ? 'text-emerald-600' : 'text-gray-400'}`}>
          {checked ? '✓' : '○'}
        </span>
      ) : icon ? (
        icon
      ) : (
        <span className="w-4" />
      )}
      {label}
    </button>
  );

  // Expandable menu item with submenu
  const ExpandableMenuItem: React.FC<{
    label: string;
    sectionId: string;
    icon?: React.ReactNode;
    children: React.ReactNode;
  }> = ({ label, sectionId, icon, children }) => (
    <div>
      <button
        className="w-full px-3 py-2 text-left text-sm flex items-center justify-between text-gray-700 hover:bg-gray-100"
        onClick={() => toggleSection(sectionId)}
      >
        <span className="flex items-center gap-2">
          {icon || <span className="w-4" />}
          {label}
        </span>
        <svg
          className={`w-4 h-4 transition-transform ${expandedSection === sectionId ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {expandedSection === sectionId && (
        <div className="bg-gray-50 border-l-2 border-blue-400 ml-3" style={{ height: '250px', overflow: 'hidden' }}>
          <div style={{ height: '100%', overflowY: 'scroll' }}>
            {children}
          </div>
        </div>
      )}
    </div>
  );

  const Divider = () => <div className="border-t border-gray-200 my-1" />;

  const SectionHeader: React.FC<{ label: string }> = ({ label }) => (
    <div className="px-3 py-1 text-xs font-semibold text-gray-500 uppercase tracking-wide bg-gray-50">
      {label}
    </div>
  );

  const PlusIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
  );

  const CalcIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
    </svg>
  );

  const EyeIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  );

  const TrashIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  );

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Toast notification */}
      {toast && (
        <div className="fixed top-4 right-4 z-[10000] px-4 py-2 bg-gray-800 text-white rounded-lg shadow-lg flex items-center gap-2">
          {isLoading && (
            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          )}
          {toast}
        </div>
      )}

      {/* Dropdown trigger button */}
      <button
        ref={buttonRef}
        onClick={() => !isLoading && setIsOpen(!isOpen)}
        disabled={isLoading}
        title="Table management — column visibility, row operations, and data tools"
        className={`px-2 py-1.5 text-xs text-white bg-white/20 border border-white/30 rounded-md hover:bg-white/30 flex items-center gap-1 transition-colors ${
          isLoading ? 'opacity-50 cursor-not-allowed' : ''
        }`}
      >
        {isLoading ? (
          <svg className="animate-spin h-3.5 w-3.5" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        ) : (
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        )}
        Table
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown menu */}
      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            style={{
              position: 'fixed',
              top: menuPos.top,
              left: menuPos.left,
              zIndex: 9999,
              maxHeight: 'calc(100vh - 100px)',
              overflowY: 'auto',
            }}
            className="w-72 bg-white rounded-lg shadow-xl border border-gray-200"
          >
            {/* COLUMN OPERATIONS */}
            <SectionHeader label="Column Operations" />

            <MenuItem label="Add Column" onClick={handleAddColumn} icon={<PlusIcon />} />

            <ExpandableMenuItem label="Add Calculated Column" sectionId="calculated" icon={<CalcIcon />}>
              {CALCULATED_COLUMNS.map((col) => (
                <button
                  key={col.id}
                  className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                  onClick={() => handleAddCalculatedColumn(col.id, col.label)}
                >
                  {col.label}
                </button>
              ))}
            </ExpandableMenuItem>

            {(() => { console.log('[HideShow] liveColumns:', liveColumns); return null; })()}
            <ExpandableMenuItem label="Hide/Show Columns" sectionId="visibility" icon={<EyeIcon />}>
              {liveColumns.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500">No columns available</div>
              ) : (
                liveColumns.map((col) => (
                  <label
                    key={col}
                    className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={visibleColumns.has(col)}
                      onChange={() => handleToggleColumn(col)}
                      className="rounded border-gray-300"
                    />
                    {col}
                  </label>
                ))
              )}
            </ExpandableMenuItem>

            <MenuItem label="Show All Columns" onClick={handleShowAllColumns} icon={<EyeIcon />} />

            <ExpandableMenuItem label="Remove Column" sectionId="remove" icon={<TrashIcon />}>
              {liveColumns.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500">No columns to remove</div>
              ) : (
                liveColumns.map((col) => (
                  <button
                    key={col}
                    className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-red-50 hover:text-red-700"
                    onClick={() => handleRemoveColumn(col)}
                  >
                    {col}
                  </button>
                ))
              )}
            </ExpandableMenuItem>

            <Divider />

            {/* ROW OPERATIONS */}
            <SectionHeader label="Row Operations" />

            <MenuItem label="Add Row (Draw)" onClick={handleAddRow} icon={<PlusIcon />} />
            <MenuItem label="Add Blank Row" onClick={handleAddBlankRow} icon={<PlusIcon />} />
            <MenuItem
              label={`Delete Selected (${selectedCount})`}
              onClick={handleDeleteSelectedRows}
              disabled={selectedCount === 0}
              icon={<TrashIcon />}
            />
            <MenuItem
              label={`Bulk Edit Selected (${selectedCount})`}
              onClick={handleBulkEdit}
              disabled={selectedCount === 0}
              icon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              }
            />

            <Divider />

            {/* DATA OPERATIONS */}
            <SectionHeader label="Data Operations" />

            <MenuItem
              label="Remove Duplicates"
              onClick={handleDeduplicateClick}
              icon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              }
            />
            <MenuItem
              label={`Copy Selected (${selectedCount})`}
              onClick={handleCopySelected}
              disabled={selectedCount === 0}
              icon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                </svg>
              }
            />
            <MenuItem
              label={`Export Selected (${selectedCount})`}
              onClick={handleExportSelected}
              disabled={selectedCount === 0}
              icon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              }
            />

            <Divider />

            {/* VIEW OPTIONS */}
            <SectionHeader label="View Options" />

            <MenuItem
              label="Show Matches Only"
              onClick={() => showComingSoon('Show Matches Only')}
              icon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
              }
            />
            <MenuItem
              label="Show All Molecules"
              onClick={() => showComingSoon('Show All Molecules')}
              icon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
              }
            />
            {/* Heatmap Settings button - opens floating panel */}
            {showHeatmap && (
              <button
                onClick={() => {
                  closeMenu();
                  onOpenHeatmapPanel?.();
                }}
                className="w-full px-3 py-2 text-left text-sm text-emerald-700 bg-emerald-50 hover:bg-emerald-100 flex items-center gap-2 border-l-2 border-emerald-400 ml-3"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                </svg>
                <span>Configure Heatmap...</span>
              </button>
            )}
            <MenuItem
              label="Reset Column Widths"
              onClick={() => {
                if (onResetColumnWidths) {
                  onResetColumnWidths();
                  setToast('Column widths reset to defaults');
                }
                closeMenu();
              }}
              icon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                </svg>
              }
            />
            <MenuItem
              label="Reset View"
              onClick={() => showComingSoon('Reset View')}
              icon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              }
            />
          </div>,
          document.body
        )}

      {/* Add Row — Ketcher structure editor */}
      {showAddRowModal && (
        <StructureEditorModal
          isOpen={showAddRowModal}
          onClose={() => setShowAddRowModal(false)}
          onSave={(molfile, smiles) => {
            setShowAddRowModal(false);
            if (!fileId) {
              setToast('No file selected');
              return;
            }
            const structure = (smiles || molfile || '').trim();
            setIsLoading(true);
            setToast('Adding molecule...');
            chemistryApi.addMolecule(fileId, { structure, molproperties: {} })
              .then((result) => {
                setToast(`Added ${result.length} molecule(s) successfully`);
                if (onRefresh) onRefresh();
              })
              .catch((error) => {
                console.error('Failed to add molecule:', error);
                setToast('Failed to add molecule');
              })
              .finally(() => setIsLoading(false));
          }}
          title="Add New Molecule"
        />
      )}

      {/* Add Column Modal */}
      {showAddColumnModal && createPortal(
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-96 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Add New Column</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Column Name
                </label>
                <input
                  type="text"
                  value={newColumnName}
                  onChange={(e) => setNewColumnName(e.target.value)}
                  placeholder="e.g., Activity, Notes, Status"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Default Value (optional)
                </label>
                <input
                  type="text"
                  value={newColumnDefault}
                  onChange={(e) => setNewColumnDefault(e.target.value)}
                  placeholder="Leave empty for blank"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setShowAddColumnModal(false)}
                  className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitAddColumn}
                  disabled={!newColumnName.trim()}
                  className="px-4 py-2 text-sm text-white bg-emerald-600 rounded-md hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Add Column
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && createPortal(
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-96 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Delete Molecules</h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-gray-700">
                    Are you sure you want to delete <span className="font-semibold">{selectedRows.length}</span> molecule{selectedRows.length !== 1 ? 's' : ''}?
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    This action cannot be undone.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  className="px-4 py-2 text-sm text-white bg-red-600 rounded-md hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Remove Column Confirmation Modal */}
      {showRemoveColumnModal && createPortal(
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-96 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Remove Column</h3>
            <div className="space-y-4">
              <p className="text-sm text-gray-700">
                Remove column <span className="font-semibold">'{removeColumnTarget}'</span>? This cannot be undone.
              </p>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => { setShowRemoveColumnModal(false); setRemoveColumnTarget(''); }}
                  className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmRemoveColumn}
                  className="px-4 py-2 text-sm text-white bg-red-600 rounded-md hover:bg-red-700"
                >
                  Remove
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Bulk Edit Modal */}
      {showBulkEditModal && createPortal(
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-96 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Bulk Edit {selectedRows.length} Molecule{selectedRows.length !== 1 ? 's' : ''}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Property Name
                </label>
                <select
                  value={bulkEditProperty}
                  onChange={(e) => setBulkEditProperty(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                >
                  <option value="">Select a property...</option>
                  {liveColumns.map(col => (
                    <option key={col} value={col}>{col}</option>
                  ))}
                  <option value="__new__">+ Add new property</option>
                </select>
              </div>
              {bulkEditProperty === '__new__' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    New Property Name
                  </label>
                  <input
                    type="text"
                    value={newPropertyName}
                    onChange={(e) => setNewPropertyName(e.target.value)}
                    placeholder="e.g., Status, Notes"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Value
                </label>
                <input
                  type="text"
                  value={bulkEditValue}
                  onChange={(e) => setBulkEditValue(e.target.value)}
                  placeholder="Enter value for all selected molecules"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  autoFocus
                />
                <p className="mt-1 text-xs text-gray-500">
                  This value will be applied to all {selectedRows.length} selected molecule{selectedRows.length !== 1 ? 's' : ''}.
                </p>
              </div>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setShowBulkEditModal(false)}
                  className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmBulkEdit}
                  disabled={!bulkEditProperty.trim() || (bulkEditProperty === '__new__' && !newPropertyName.trim())}
                  className="px-4 py-2 text-sm text-white bg-emerald-600 rounded-md hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Apply to All
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default TableManagementDropdown;
